import type { CSSProperties } from 'react';
import { VIEW_MODES, type ViewMode } from '../three/cameraPoses';
import { buildClassName } from './buildClassName';

interface ViewModeEntry {
  mode: ViewMode;
  label: string;
}

const VIEW_MODE_ENTRIES: ViewModeEntry[] = [
  { mode: VIEW_MODES.EXTERIOR, label: '외관' },
  { mode: VIEW_MODES.INTERIOR, label: '내부' },
];

export interface ViewModeToggleProps {
  viewMode: string;
  onViewModeChange: (mode: ViewMode) => void;
  /**
   * 실내 지오메트리가 없는 에셋에서는 false.
   * 버튼을 숨기지 않고 비활성화만 하는 이유는, 사라지면 레이아웃이 흔들려서
   * 차를 바꿀 때마다 패널이 덜컥거리기 때문입니다.
   */
  interiorAvailable?: boolean;
  /**
   * 주행 모드에서는 시점이 고정이라 두 버튼을 모두 잠급니다.
   * interiorAvailable과 나눠 둔 이유는 잠긴 사유가 달라서입니다 — 이쪽은 에셋의
   * 한계이고 저쪽은 지금 모드의 제약이라, 안내 문구도 달라야 합니다.
   */
  disabled?: boolean;
}

export function ViewModeToggle({
  viewMode,
  onViewModeChange,
  interiorAvailable = true,
  disabled = false,
}: ViewModeToggleProps) {
  const selectedIndex = VIEW_MODE_ENTRIES.findIndex((entry) => entry.mode === viewMode);
  return (
    <div className="view-mode-toggle" role="group" aria-label="보기 모드">
      <span
        className="view-mode-toggle__thumb"
        aria-hidden="true"
        // 커스텀 프로퍼티는 CSSProperties에 없어서 여기서 한 번 좁혀 줍니다.
        style={{ '--tds-segment-index': Math.max(selectedIndex, 0) } as CSSProperties}
      />
      {VIEW_MODE_ENTRIES.map((entry) => {
        const isInteriorEntry = entry.mode === VIEW_MODES.INTERIOR;
        const isMissingInterior = isInteriorEntry && !interiorAvailable;
        const isDisabled = disabled || isMissingInterior;
        /*
         * 주행 모드로 잠긴 것뿐이라면 지금 시점 표시는 남겨 둡니다. 표시까지 지우면
         * 보기 모드로 나올 때 어디로 돌아갈지 알 수 없습니다. 실내가 없는 모델일
         * 때만 선택 표시를 뗍니다(돌아갈 시점 자체가 없습니다).
         */
        const isSelected = entry.mode === viewMode && !isMissingInterior;
        return (
          <button
            key={entry.mode}
            type="button"
            className={buildClassName('view-mode-toggle__button', 'view-mode-toggle__button--selected', isSelected)}
            aria-pressed={isSelected}
            disabled={isDisabled}
            title={isMissingInterior ? '이 모델은 실내 지오메트리가 없는 외관 전용 에셋입니다' : undefined}
            onClick={() => onViewModeChange(entry.mode)}
          >
            {entry.label}
          </button>
        );
      })}
    </div>
  );
}
